import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { fetchTickets } from '../api/api';
import { useAuth } from './AuthContext';
import { useSocket } from './SocketContext';

const NotificationContext = createContext();

const toIsoString = (value) => {
  if (!value) return new Date().toISOString();
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? new Date().toISOString() : d.toISOString();
};

const safeText = (value, fallback = '') => {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return fallback;
};

const storageKeyForUser = (userId) => `nextstep.notifications.${userId}`;

const toStatusNotification = (ticket) => {
  const ticketId = safeText(ticket?.id);
  if (!ticketId) return null;

  const status = safeText(ticket?.status).toLowerCase();
  const updatedAt = toIsoString(ticket?.updated_at ?? ticket?.updatedAt ?? ticket?.created_at ?? ticket?.createdAt);
  const sid = `NXP-SVC-${String(ticketId).padStart(6, '0')}`;

  if (status === 'resolved' || status === 'closed') {
    return {
      id: `ticket-resolved-${ticketId}-${updatedAt}`,
      type: 'success',
      title: 'Complaint Resolved',
      message: `${sid} has been resolved successfully.`,
      createdAt: updatedAt,
      read: false,
      ticketId,
    };
  }

  if (status === 'in-progress') {
    return {
      id: `ticket-progress-${ticketId}-${updatedAt}`,
      type: 'info',
      title: 'Work In Progress',
      message: `${sid} is in progress.`,
      createdAt: updatedAt,
      read: false,
      ticketId,
    };
  }

  if (status === 'open') {
    return {
      id: `ticket-open-${ticketId}-${updatedAt}`,
      type: 'info',
      title: 'Complaint Logged',
      message: `${sid} has been logged and is waiting for assignment.`,
      createdAt: toIsoString(ticket?.created_at ?? ticket?.createdAt ?? updatedAt),
      read: false,
      ticketId,
    };
  }

  return null;
};

const fromSocketEvent = (eventName, payload) => {
  console.log('[FROM SOCKET EVENT] Processing:', eventName, payload);

  const ticket = payload?.ticket ?? payload ?? {};
  const ticketId = safeText(ticket?.id);
  const updatedAt = toIsoString(ticket?.updated_at ?? ticket?.updatedAt ?? payload?.updatedAt ?? payload?.createdAt);
  const sid = ticketId ? `NXP-SVC-${String(ticketId).padStart(6, '0')}` : 'Complaint';

  console.log('[FROM SOCKET EVENT] Extracted:', { ticketId, updatedAt, sid, eventName });

  if (eventName === 'ticket:created' && ticketId) {
    return {
      id: `evt-created-${ticketId}-${updatedAt}`,
      type: 'info',
      title: 'New Complaint',
      message: `New complaint ${sid} has been created.`,
      createdAt: updatedAt,
      read: false,
      ticketId,
    };
  }

  if (eventName === 'ticket:assigned' && ticketId) {
    const engineerName = safeText(ticket?.engineer_name ?? ticket?.engineerName ?? payload?.engineerName);
    const engineerSuffix = engineerName ? ` to ${engineerName}` : '';
    console.log('[NOTIFICATION] Ticket assigned:', { ticketId, engineerName, sid });
    return {
      id: `evt-assigned-${ticketId}-${updatedAt}`,
      type: 'info',
      title: 'Complaint Assigned',
      message: `${sid} has been assigned${engineerSuffix}.`,
      createdAt: updatedAt,
      read: false,
      ticketId,
    };
  }

  if ((eventName === 'ticket:updated' || eventName === 'ticket:completed') && ticketId) {
    const status = safeText(ticket?.status).toLowerCase();
    const hasFeedback = ticket?.rating || ticket?.feedback_comment;

    // Check if this is a feedback submission
    if (hasFeedback) {
      return {
        id: `evt-feedback-${ticketId}-${updatedAt}`,
        type: 'info',
        title: 'Feedback Submitted',
        message: `${sid} has received customer feedback.`,
        createdAt: updatedAt,
        read: false,
        ticketId,
      };
    }

    // Check if resolved
    if (status === 'resolved' || eventName === 'ticket:completed') {
      return {
        id: `evt-completed-${ticketId}-${updatedAt}`,
        type: 'success',
        title: 'Complaint Resolved',
        message: `${sid} has been marked as resolved.`,
        createdAt: updatedAt,
        read: false,
        ticketId,
      };
    }

    // General update
    return {
      id: `evt-updated-${ticketId}-${updatedAt}`,
      type: 'info',
      title: 'Complaint Updated',
      message: `${sid} has a new status update.`,
      createdAt: updatedAt,
      read: false,
      ticketId,
    };
  }

  console.log('[FROM SOCKET EVENT] No notification created for:', eventName);
  return null;
};

const sortByNewest = (items) =>
  [...items].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

const mergeUnique = (current, incoming) => {
  const seen = new Set(current.map((n) => n.id));
  const merged = [...current];
  incoming.forEach((n) => {
    if (!seen.has(n.id)) {
      seen.add(n.id);
      merged.push(n);
    }
  });
  return sortByNewest(merged);
};

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const socket = useSocket();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  const persist = useCallback(
    (nextItems) => {
      if (!user?.id) return;
      try {
        localStorage.setItem(storageKeyForUser(String(user.id)), JSON.stringify(nextItems));
      } catch {
        // Ignore storage failures to avoid breaking UX.
      }
    },
    [user?.id]
  );

  const refresh = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const tickets = await fetchTickets();
      const seeded = tickets
        .map((t) => toStatusNotification(t))
        .filter((n) => Boolean(n));

      setNotifications((prev) => {
        const next = mergeUnique(prev, seeded);
        persist(next);
        return next;
      });
    } catch {
      // Keep existing notifications if refresh fails.
    } finally {
      setLoading(false);
    }
  }, [persist, user?.id]);

  useEffect(() => {
    let mounted = true;
    const restore = () => {
      if (!user?.id) {
        setNotifications([]);
        return;
      }
      try {
        const raw = localStorage.getItem(storageKeyForUser(String(user.id)));
        if (!mounted) return;
        if (!raw) {
          setNotifications([]);
          return;
        }
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          setNotifications(sortByNewest(parsed));
        } else {
          setNotifications([]);
        }
      } catch {
        if (mounted) setNotifications([]);
      }
    };

    restore();
    return () => {
      mounted = false;
    };
  }, [user?.id]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!socket || !user?.id) return;

    const onEvent = (eventName) => (payload) => {
      console.log('[SOCKET EVENT] Received:', eventName, payload);
      console.log('[SOCKET EVENT] User role:', user?.role);
      console.log('[SOCKET EVENT] User ID:', user?.id);

      const next = fromSocketEvent(eventName, payload);
      if (!next) {
        console.log('[SOCKET EVENT] No notification created for event:', eventName);
        return;
      }

      console.log('[SOCKET EVENT] Notification created:', next);

      // Show browser notification if permission granted
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification(next.title, {
          body: next.message,
          icon: '/favicon.ico',
        });
      }

      setNotifications((prev) => {
        if (prev.some((n) => n.id === next.id)) return prev;
        const updated = sortByNewest([next, ...prev]);
        persist(updated);
        return updated;
      });
    };

    const handlers = [
      ['ticket:created', onEvent('ticket:created')],
      ['ticket:assigned', onEvent('ticket:assigned')],
      ['ticket:updated', onEvent('ticket:updated')],
      ['ticket:completed', onEvent('ticket:completed')],
    ];

    handlers.forEach(([event, handler]) => socket.on(event, handler));

    return () => {
      handlers.forEach(([event, handler]) => socket.off(event, handler));
    };
  }, [persist, socket, user?.id]);

  // Request browser notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  const markAsRead = useCallback(
    (id) => {
      setNotifications((prev) => {
        const next = prev.map((n) => (n.id === id ? { ...n, read: true } : n));
        persist(next);
        return next;
      });
    },
    [persist]
  );

  const markAllAsRead = useCallback(() => {
    setNotifications((prev) => {
      const next = prev.map((n) => ({ ...n, read: true }));
      persist(next);
      return next;
    });
  }, [persist]);

  const clearAll = useCallback(() => {
    setNotifications([]);
    persist([]);
  }, [persist]);

  const value = useMemo(
    () => ({
      notifications,
      unreadCount: notifications.filter((n) => !n.read).length,
      loading,
      markAsRead,
      markAllAsRead,
      clearAll,
      refresh,
    }),
    [clearAll, loading, markAllAsRead, markAsRead, notifications, refresh]
  );

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};
